import { Navigate, useLocation } from 'react-router'
import type { JSX, ReactNode } from 'react'

import B2becSpinner from '@/components/B2becSpinner'
import { useAppSelector } from '@/store/hook'
import { selectShouldRenderServiceMenu } from '@/store/selectors/configSelector'
import {
  selectIsCheckingAuthentication,
  selectIsLoggedIn,
  selectUserId,
} from '@/store/slices/authenticationSlice'
import usePermission from '@/hooks/usePermission'
import { linkGenerator } from '@/libs/utils/language'

const HOME_PATH = '/'
const SERVICE_ROUTES_PATTERN = /request-service|service-packages/

type PermissionType = string | string[] | undefined

interface RequireAuthProps {
  children: ReactNode
  requiredPermission?: PermissionType
}

const RequireAuth = ({ children, requiredPermission }: RequireAuthProps): JSX.Element => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const userId = useAppSelector(selectUserId)
  const shouldRenderServiceMenu = useAppSelector(selectShouldRenderServiceMenu)
  const { hasPermission } = usePermission(requiredPermission)
  const location = useLocation()

  if (!isUserAuthenticated(isLoggedIn, userId)) {
    return <Navigate replace state={{ from: location }} to={linkGenerator('')} />
  }

  if (
    hasRequiredPermissionButNotAuthorized(requiredPermission, hasPermission)
  ) {
    return <Navigate replace state={{ from: location }} to={linkGenerator('/unauthorized')} />
  }

  if (
    isServiceRouteButMenuDisabled(location.pathname, shouldRenderServiceMenu)
  ) {
    return <Navigate replace state={{ from: location }} to={linkGenerator('/not-found')} />
  }

  return <>{children}</>
}

function isUserAuthenticated(isLoggedIn: boolean, userId: string | undefined): boolean {
  return isLoggedIn || !!userId
}

/**
 * Checks if permission requirements are not satisfied when permissions are specified
 */
function hasRequiredPermissionButNotAuthorized(
  requiredPermission: PermissionType,
  hasPermission: boolean,
): boolean {
  return !!requiredPermission && !hasPermission
}

/**
 * Verifies if the current route is a service route but the service menu is disabled
 */
function isServiceRouteButMenuDisabled(
  pathname: string,
  shouldRenderServiceMenu: boolean,
): boolean {
  return !shouldRenderServiceMenu && SERVICE_ROUTES_PATTERN.test(pathname)
}

export default RequireAuth
